import { makeAuthenticatedApiCall } from '../apiHelper';
import { describePieceDetachError, isPieceNotReferenced } from './cfnPieces';

export interface PieceDetachmentResult {
  /**
   * `true` quand le serveur a retiré la pièce ET pris en charge l'effacement du
   * fichier Storage (droits admin) : le navigateur ne doit pas le refaire. Les
   * règles Storage refusent la suppression à tout autre que le déposant, d'où
   * le faux message d'erreur quand un GOD retirait un dépôt agence (GM-079-TC).
   */
  storageHandledByServer: boolean;
}

/**
 * Retire une pièce du dossier côté base (prop `onBeforeDelete` de
 * `FirebaseUploadComponent`) ; le serveur efface aussi le fichier Storage.
 * Lève une Error dont le message est affichable telle quelle ; un 404 (aucune
 * référence en base) ne bloque pas : rien à retirer, le navigateur efface
 * lui-même le fichier comme avant.
 */
export const requestPieceDetachment = async (
  endpoint: string,
  url: string,
  options: { allPieces?: boolean } = {},
): Promise<PieceDetachmentResult> => {
  try {
    await makeAuthenticatedApiCall('delete', endpoint, options.allPieces ? { url, allPieces: true } : { url });
    return { storageHandledByServer: true };
  } catch (error) {
    if (isPieceNotReferenced(error)) return { storageHandledByServer: false };
    if (isSharedFileConflict(error)) throw new SharedFileError(describePieceDetachError(error));
    throw new Error(describePieceDetachError(error));
  }
};

/**
 * Le fichier porte plusieurs pièces du dossier (PDF scanné d'un bloc) : le
 * serveur refuse de le retirer sans `allPieces`, qui les effacerait toutes.
 */
export class SharedFileError extends Error {}

const isSharedFileConflict = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return response?.status === 409 && response.data?.code === 'SHARED_FILE';
};

/** Endpoints de retrait, un par source de pièces (même contrat `{ url }`). */
export const pieceDetachmentEndpoints = {
  cg: (registrationId: number | string) => `/api/cg/${registrationId}/documents`,
  da: (registrationId: number | string) => `/api/da/${registrationId}/documents`,
  deal: (pipedriveDealId: number | string) => `/api/deals/${pipedriveDealId}/procurement-file`,
  depositSaleBuyer: (pipedriveDealId: number | string) => `/api/misiv/deposit-sale-buyer/${pipedriveDealId}/files`,
};
